import type {
  OrderResponse,
  PatchOrderRequest,
} from '@nebalkon-workspace/order-api';
import {
  datetimeLocalValueToIso,
  isoDatesEqualMinute,
  isoToDatetimeLocalValue,
} from './staff-order-datetime.util';

export interface StaffOrderEditDialogData {
  order: OrderResponse;
}

export type StaffOrderEditDialogClose =
  | { kind: 'saved'; order: OrderResponse }
  | undefined;

export interface StaffOrderEditFormSnapshot {
  pickup_address: string;
  delivery_address: string;
  scheduled_at: string;
  items_description: string;
  access_description: string;
  return_to_warehouse: boolean;
  warehouse_id: string;
  reason: string;
}

export function orderToStaffOrderEditFormSnapshot(
  order: OrderResponse,
): StaffOrderEditFormSnapshot {
  return {
    pickup_address: order.pickup_address?.trim() ?? '',
    delivery_address: order.delivery_address?.trim() ?? '',
    scheduled_at: isoToDatetimeLocalValue(order.scheduled_at),
    items_description: order.items_description?.trim() ?? '',
    access_description: order.access_description?.trim() ?? '',
    return_to_warehouse: order.return_to_warehouse,
    warehouse_id: order.warehouse_id?.trim() ?? '',
    reason: '',
  };
}

export function buildStaffOrderEditPatch(
  original: OrderResponse,
  form: StaffOrderEditFormSnapshot,
): PatchOrderRequest | null {
  const patch: PatchOrderRequest = {};

  const pickupAddress = form.pickup_address.trim();
  if (pickupAddress !== (original.pickup_address?.trim() ?? '')) {
    patch.pickup_address = pickupAddress;
  }

  const deliveryAddress = form.delivery_address.trim();
  if (deliveryAddress !== (original.delivery_address?.trim() ?? '')) {
    patch.delivery_address = deliveryAddress;
  }

  const scheduledIso = datetimeLocalToPatchIso(
    form.scheduled_at,
    original.scheduled_at,
  );
  if (scheduledIso.changed) {
    patch.scheduled_at = scheduledIso.value;
  }

  const itemsDescription = form.items_description.trim();
  if (itemsDescription !== (original.items_description?.trim() ?? '')) {
    patch.items_description = itemsDescription;
  }

  const accessDescription = form.access_description.trim();
  if (accessDescription !== (original.access_description?.trim() ?? '')) {
    patch.access_description = accessDescription;
  }

  if (form.return_to_warehouse !== original.return_to_warehouse) {
    patch.return_to_warehouse = form.return_to_warehouse;
  }

  const warehouseId = form.warehouse_id.trim();
  if (warehouseId !== (original.warehouse_id?.trim() ?? '')) {
    patch.warehouse_id = warehouseId || undefined;
  }

  if (Object.keys(patch).length > 0) {
    patch.reason = form.reason.trim();
  }

  return Object.keys(patch).length > 0 ? patch : null;
}

function datetimeLocalToPatchIso(
  localValue: string,
  originalIso?: string,
): { changed: boolean; value?: string } {
  const nextIso = localValue.trim()
    ? datetimeLocalValueToIso(localValue)
    : undefined;
  const changed = !isoDatesEqualMinute(originalIso, nextIso ?? null);
  if (!changed) {
    return { changed: false };
  }
  return { changed: true, value: nextIso };
}
